import type { ApiClient } from "@/api/client";
import { API_VERSION_PREFIX, newIdempotencyKey } from "@/api/client";
import type {
  AppealAction,
  AppealDto,
  EnforcementDto,
  EnforcementLifecycleAction,
  InternalNoteDto,
  ListParams,
  ModerationCaseAction,
  ModerationCaseDto,
  CaseEvidenceDto,
  CasePolicyDto,
  CaseSubjectDto,
  PaginationMeta,
  ReportAction,
  ReportDto,
  RequestMeta,
} from "./types";

const BASE = `${API_VERSION_PREFIX}/hq/trust-safety`;

function toQuery(params: ListParams = {}): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === "") continue;
    search.set(key, String(value));
  }
  const qs = search.toString();
  return qs ? `?${qs}` : "";
}

export function createTrustSafetyApi(client: ApiClient) {
  return {
    listReports(params: ListParams = {}) {
      return client.get<{
        reports: ReportDto[];
        pagination: PaginationMeta;
        meta: RequestMeta;
      }>(`${BASE}/reports${toQuery(params)}`);
    },

    getReport(id: string) {
      return client.get<{ report: ReportDto; meta: RequestMeta }>(
        `${BASE}/reports/${id}`,
      );
    },

    createReport(body: Record<string, unknown>) {
      return client.post<{ report: ReportDto; meta: RequestMeta }>(
        `${BASE}/reports`,
        body,
        newIdempotencyKey(),
      );
    },

    applyReportAction(
      id: string,
      action: ReportAction,
      body: Record<string, unknown>,
    ) {
      return client.post(
        `${BASE}/reports/${id}/actions/${action}`,
        body,
        newIdempotencyKey(),
      );
    },

    listCases(params: ListParams = {}) {
      return client.get<{
        moderation_cases: ModerationCaseDto[];
        pagination: PaginationMeta;
        meta: RequestMeta;
      }>(`${BASE}/cases${toQuery(params)}`);
    },

    getCase(id: string) {
      return client.get<{
        moderation_case: ModerationCaseDto;
        meta: RequestMeta;
      }>(`${BASE}/cases/${id}`);
    },

    createCase(body: Record<string, unknown>) {
      return client.post<{
        moderation_case: ModerationCaseDto;
        meta: RequestMeta;
      }>(`${BASE}/cases`, body, newIdempotencyKey());
    },

    applyCaseAction(
      id: string,
      action: ModerationCaseAction,
      body: Record<string, unknown>,
    ) {
      return client.post(
        `${BASE}/cases/${id}/actions/${action}`,
        body,
        newIdempotencyKey(),
      );
    },

    listSubjects(caseId: string, params: ListParams = {}) {
      return client.get<{
        subjects: CaseSubjectDto[];
        pagination: PaginationMeta;
        meta: RequestMeta;
      }>(`${BASE}/cases/${caseId}/subjects${toQuery(params)}`);
    },

    addSubject(caseId: string, body: Record<string, unknown>) {
      return client.post(
        `${BASE}/cases/${caseId}/subjects`,
        body,
        newIdempotencyKey(),
      );
    },

    listEvidence(caseId: string, params: ListParams = {}) {
      return client.get<{
        evidence: CaseEvidenceDto[];
        pagination: PaginationMeta;
        meta: RequestMeta;
      }>(`${BASE}/cases/${caseId}/evidence${toQuery(params)}`);
    },

    addEvidence(caseId: string, body: Record<string, unknown>) {
      return client.post(
        `${BASE}/cases/${caseId}/evidence`,
        body,
        newIdempotencyKey(),
      );
    },

    listPolicies(caseId: string, params: ListParams = {}) {
      return client.get<{
        policy_mappings: CasePolicyDto[];
        pagination: PaginationMeta;
        meta: RequestMeta;
      }>(`${BASE}/cases/${caseId}/policies${toQuery(params)}`);
    },

    mapPolicy(caseId: string, body: Record<string, unknown>) {
      return client.post(
        `${BASE}/cases/${caseId}/policies`,
        body,
        newIdempotencyKey(),
      );
    },

    listCaseNotes(caseId: string, params: ListParams = {}) {
      return client.get<{
        internal_notes: InternalNoteDto[];
        pagination: PaginationMeta;
        meta: RequestMeta;
      }>(`${BASE}/cases/${caseId}/internal-notes${toQuery(params)}`);
    },

    createCaseNote(caseId: string, body: Record<string, unknown>) {
      return client.post(
        `${BASE}/cases/${caseId}/internal-notes`,
        body,
        newIdempotencyKey(),
      );
    },

    listCaseEnforcements(caseId: string, params: ListParams = {}) {
      return client.get<{
        enforcement_actions: EnforcementDto[];
        pagination: PaginationMeta;
        meta: RequestMeta;
      }>(`${BASE}/cases/${caseId}/enforcements${toQuery(params)}`);
    },

    proposeEnforcement(caseId: string, body: Record<string, unknown>) {
      return client.post(
        `${BASE}/cases/${caseId}/enforcements`,
        body,
        newIdempotencyKey(),
      );
    },

    listEnforcements(params: ListParams = {}) {
      return client.get<{
        enforcement_actions: EnforcementDto[];
        pagination: PaginationMeta;
        meta: RequestMeta;
      }>(`${BASE}/enforcements${toQuery(params)}`);
    },

    applyEnforcementAction(
      id: string,
      action: EnforcementLifecycleAction,
      body: Record<string, unknown>,
    ) {
      return client.post(
        `${BASE}/enforcements/${id}/actions/${action}`,
        body,
        newIdempotencyKey(),
      );
    },

    listAppeals(params: ListParams = {}) {
      return client.get<{
        appeals: AppealDto[];
        pagination: PaginationMeta;
        meta: RequestMeta;
      }>(`${BASE}/appeals${toQuery(params)}`);
    },

    getAppeal(id: string) {
      return client.get<{ appeal: AppealDto; meta: RequestMeta }>(
        `${BASE}/appeals/${id}`,
      );
    },

    submitAppeal(body: Record<string, unknown>) {
      return client.post<{ appeal: AppealDto; meta: RequestMeta }>(
        `${BASE}/appeals`,
        body,
        newIdempotencyKey(),
      );
    },

    applyAppealAction(
      id: string,
      action: AppealAction,
      body: Record<string, unknown>,
    ) {
      return client.post(
        `${BASE}/appeals/${id}/actions/${action}`,
        body,
        newIdempotencyKey(),
      );
    },
  };
}

export type TrustSafetyApi = ReturnType<typeof createTrustSafetyApi>;
